import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Head from "../../../layout/head/Head";
import Content from "../../../layout/content/Content";
import PageHeading from "../components/page_heading/PageHeading";
import WizardComp from "../components/wizard/WizardComp";
import * as app_consts from "../../../constants/react_app_consts";
import { useLocation, useParams } from "react-router";
import { useDispatch } from "react-redux";

import { Modal, ModalBody, ModalHeader, Button, Alert } from "reactstrap";
import WeighbridgeTicketSelector from "../weighbridge/WeighbridgeTicketSelector";
import SelectedWeighbridgeTicketCard from "../weighbridge/SelectedWeighbridgeTicketCard";
import AuthLoader from "../../components/page_messaging/AuthLoader";

import { RETRIEVE_ADD_GOODS_RECEIVED_FROM_NOT_SET_WEIGHBRIDGE_TICKET_DATA_API } from "../../../api/api_data";
import { makeEmployeeDashboardApiRequest } from "../../../api/api_helper";
import { getLastWeighbridgeSyncMeta } from "../../../services/weighbridge_sync_service";
import { success_sign_out_modal } from "../../../reducers/dashboard_reducers/auth/sign_out_modal_Slice";
import { mapWeighbridgeWeightsToGrn } from "../../../utils/weighbridgeUtils";
import {
  resolveWeighbridgeTicketPrefillData,
  resolveWeighbridgeUsageStatus,
  resolveWeighbridgeTicketRecord,
  sortWeighbridgeTicketsDesc,
} from "../../../utils/weighbridgeTicketNormalization";

const normalizeWeighbridgeTickets = (rows = []) => {
  return rows.map((item, index) => {
    const { ticket, ticketStatus, ticketType, backendTicketId } =
      resolveWeighbridgeTicketRecord(item);
    const prefillData = resolveWeighbridgeTicketPrefillData(item);
    const weighBridgeTicketStatusId =
      ticket?.weigh_bridge_ticket_status_id ||
      item?.weigh_bridge_ticket_status_id ||
      ticketStatus?.weigh_bridge_ticket_status_id ||
      "";
    const weighBridgeTicketStatus =
      prefillData?.weigh_bridge_ticket_status ||
      ticketStatus?.weigh_bridge_ticket_status ||
      ticket?.weigh_bridge_ticket_status ||
      (typeof item?.weigh_bridge_ticket_status === "string"
        ? item.weigh_bridge_ticket_status
        : "") ||
      (typeof item?.ticket_status === "string" ? item.ticket_status : "") ||
      (typeof item?.status === "string" ? item.status : "") ||
      "";
    const weighBridgeTicketTypeId =
      ticket?.weigh_bridge_ticket_type_id ||
      item?.weigh_bridge_ticket_type_id ||
      ticketType?.weigh_bridge_ticket_type_id ||
      "";
    const weighBridgeTicketType =
      prefillData?.weigh_bridge_ticket_type ||
      ticketType?.weigh_bride_ticket_type ||
      ticketType?.weigh_bridge_ticket_type ||
      ticket?.weigh_bridge_ticket_type ||
      (typeof item?.weigh_bridge_ticket_type === "string"
        ? item.weigh_bridge_ticket_type
        : "") ||
      (typeof item?.ticket_type === "string" ? item.ticket_type : "") ||
      "";
    const grnWeights = mapWeighbridgeWeightsToGrn(prefillData);

    return {
      id:
        backendTicketId ||
        prefillData?.weighbridge_ticket_id ||
        prefillData?.weigh_bridge_ticket_number ||
        prefillData?.weigh_bridge_ticket_reference_no ||
        index,
      weighbridge_ticket_id: backendTicketId || prefillData?.weighbridge_ticket_id || "",

      raw: item,

      weigh_bridge_ticket_number: prefillData?.weigh_bridge_ticket_number || "",
      weigh_bridge_ticket_reference_no: prefillData?.weigh_bridge_ticket_reference_no || "",
      external_weigh_bridge_ticket_number: prefillData?.external_weigh_bridge_ticket_number || "",

      first_weight: grnWeights.first_weight,
      second_weight: grnWeights.second_weight,
      net_weight: grnWeights.net_weight,

      time_in: prefillData?.time_in || ticket?.time_in || "",
      time_out: prefillData?.time_out || ticket?.time_out || "",
      created_on: prefillData?.created_on || ticket?.created_on || "",

      truck_driver_name: prefillData?.truck_driver_name || "",
      transporter_name: prefillData?.transporter_name || "",
      truck_licence_plate: prefillData?.truck_licence_plate || "",

      designation: prefillData?.designation || "",
      usage_status: resolveWeighbridgeUsageStatus(item),

      weigh_bridge_ticket_status_id: weighBridgeTicketStatusId,
      weigh_bridge_ticket_status: weighBridgeTicketStatus,

      branch_location_id: prefillData?.branch_location_id || "",
      branch_location_name: prefillData?.branch_location_name || "",
      source_system: prefillData?.source_system || "",
      sync_batch_id: prefillData?.sync_batch_id || "",
      trailer_one: prefillData?.trailer_one || "",
      trailer_two: prefillData?.trailer_two || "",
      comments: prefillData?.comments || "",
      raw_payload: prefillData?.raw_payload || null,

      weigh_bridge_ticket_type_id: weighBridgeTicketTypeId,
      weigh_bridge_ticket_type: weighBridgeTicketType,
    };
  });
};

const normalizeGrnSelectedTicket = (ticket = null) => {
  if (!ticket) {
    return null;
  }

  const selectedTicketPrefill = resolveWeighbridgeTicketPrefillData(ticket);
  const grnWeights = mapWeighbridgeWeightsToGrn(selectedTicketPrefill);

  return {
    ...ticket,
    ...selectedTicketPrefill,
    ...grnWeights,
    id: ticket?.id || selectedTicketPrefill?.weighbridge_ticket_id || "",
    raw: ticket?.raw || ticket,
  };
};

const flattenBranchGroupedTickets = (rows = []) =>
  rows.flatMap((item) => {
    const nestedTickets = Array.isArray(item?.weigh_bridge_tickets)
      ? item.weigh_bridge_tickets
      : Array.isArray(item?.weighbridge_tickets)
        ? item.weighbridge_tickets
        : Array.isArray(item?.tickets)
          ? item.tickets
          : [];

    if (!nestedTickets.length) {
      if (item?.tickets && typeof item.tickets === "object" && !Array.isArray(item.tickets)) {
        return [
          {
            ...item.tickets,
            raw: item,
            branch_location_id:
              item?.tickets?.branch_location_id || item?.branch_location_id || "",
            branch_area: item?.tickets?.branch_area || item?.branch_area || "",
            province_id: item?.tickets?.province_id || item?.province_id || "",
            province_name: item?.tickets?.province_name || item?.province_name || "",
          },
        ];
      }

      return [item];
    }

    return nestedTickets.map((ticketRow) => ({
      ...ticketRow,
      branch_location_id:
        ticketRow?.branch_location_id || item?.branch_location_id || "",
      branch_area: ticketRow?.branch_area || item?.branch_area || "",
      province_id: ticketRow?.province_id || item?.province_id || "",
      province_name: ticketRow?.province_name || item?.province_name || "",
    }));
  });

const looksLikeTicketGroupRow = (item = {}) =>
  Boolean(
    Array.isArray(item?.weigh_bridge_tickets) ||
      Array.isArray(item?.weighbridge_tickets) ||
      Array.isArray(item?.tickets),
  );

const looksLikeTicketRow = (item = {}) =>
  Boolean(
    item?.ticket ||
      item?.tickets ||
      looksLikeTicketGroupRow(item) ||
      item?.weigh_bridge_ticket ||
      item?.weighbridge_ticket ||
      item?.weigh_bridge_ticket_number ||
      item?.weigh_bridge_ticket_reference_no ||
      item?.external_weigh_bridge_ticket_number,
  );

const getTicketArrayCandidateScore = (path = "") => {
  const normalizedPath = String(path || "")
    .trim()
    .replaceAll(/[.[\]]+/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();

  if (!normalizedPath) {
    return 0;
  }

  let score = 0;

  if (normalizedPath.includes("not set")) score += 12;
  if (normalizedPath.includes("unattached")) score += 10;
  if (normalizedPath.includes("available")) score += 8;
  if (normalizedPath.includes("eligible")) score += 8;
  if (normalizedPath.includes("unassigned")) score += 7;
  if (normalizedPath.includes("unused")) score += 7;
  if (normalizedPath.includes("ticket")) score += 3;
  if (normalizedPath.includes("weigh")) score += 2;

  if (normalizedPath.includes("designated")) score -= 9;
  if (normalizedPath.includes("attached")) score -= 8;
  if (normalizedPath.includes("used")) score -= 8;
  if (normalizedPath.includes("processed")) score -= 6;
  if (normalizedPath.includes("goods received")) score -= 4;
  if (normalizedPath.includes("delivery note")) score -= 4;

  return score;
};

const isTicketCollection = (items = []) =>
  Array.isArray(items) &&
  items.some((item) => looksLikeTicketRow(item) || looksLikeTicketGroupRow(item));

const extractTicketList = (responseData) => {
  const queue = [{ value: responseData, path: "root" }];
  const visited = new Set();
  let fallbackRows = [];
  let fallbackPath = "root";
  let bestCandidate = null;

  while (queue.length > 0) {
    const { value: current, path } = queue.shift();

    if (!current || visited.has(current)) {
      continue;
    }

    if (typeof current === "object") {
      visited.add(current);
    }

    if (Array.isArray(current)) {
      if (isTicketCollection(current)) {
        const normalizedRows = flattenBranchGroupedTickets(current);
        const candidateScore = getTicketArrayCandidateScore(path);

        if (!fallbackRows.length) {
          fallbackRows = normalizedRows;
          fallbackPath = path;
        }

        if (!bestCandidate || candidateScore > bestCandidate.score) {
          bestCandidate = {
            rows: normalizedRows,
            path,
            score: candidateScore,
          };
        }
      }

      current.forEach((item, index) => {
        if (item && typeof item === "object") {
          queue.push({ value: item, path: `${path}[${index}]` });
        }
      });
      continue;
    }

    if (typeof current !== "object") {
      continue;
    }

    const arrayCandidateEntries = [
      ["weigh_bridge_tickets", current?.weigh_bridge_tickets],
      ["weighbridge_tickets", current?.weighbridge_tickets],
      ["tickets", current?.tickets],
      ["rows", current?.rows],
      ["items", current?.items],
      ["data", current?.data],
    ];

    for (const [candidateKey, candidate] of arrayCandidateEntries) {
      if (Array.isArray(candidate) && candidate.length > 0) {
        const candidatePath = `${path}.${candidateKey}`;

        if (isTicketCollection(candidate)) {
          const normalizedRows = flattenBranchGroupedTickets(candidate);
          const candidateScore = getTicketArrayCandidateScore(candidatePath);

          if (!fallbackRows.length) {
            fallbackRows = normalizedRows;
            fallbackPath = candidatePath;
          }

          if (!bestCandidate || candidateScore > bestCandidate.score) {
            bestCandidate = {
              rows: normalizedRows,
              path: candidatePath,
              score: candidateScore,
            };
          }
        }

        queue.push({ value: candidate, path: candidatePath });
      }
    }

    [
      ["data", current?.data],
      ["payload", current?.payload],
      ["result", current?.result],
      ["context", current?.context],
      ["weigh_bridge_tickets_data", current?.weigh_bridge_tickets_data],
    ].forEach(([candidateKey, candidate]) => {
      if (candidate && typeof candidate === "object") {
        queue.push({ value: candidate, path: `${path}.${candidateKey}` });
      }
    });
  }

  if (bestCandidate?.rows?.length) {
    return bestCandidate;
  }

  return {
    rows: fallbackRows,
    path: fallbackRows.length ? fallbackPath : "",
    score: fallbackRows.length ? getTicketArrayCandidateScore(fallbackPath) : Number.NEGATIVE_INFINITY,
  };
};

const extractNotSetWeighbridgeContext = (responseData = {}) => {
  if (Array.isArray(responseData)) {
    return responseData;
  }

  if (responseData?.data && typeof responseData.data === "object" && !Array.isArray(responseData.data)) {
    return responseData.data;
  }

  if (typeof responseData === "object" && !Array.isArray(responseData)) {
    return responseData;
  }

  return {};
};

const isSelectableGrnWeighbridgeTicket = (ticket = {}) => {
  return !getGrnWeighbridgeIneligibilityReason(ticket);
};

const getReadableGrnTicketText = (value = "") =>
  String(value || "")
    .trim()
    .replaceAll("_", " ")
    .replace(/\s+/g, " ");

const getGrnWeighbridgeIneligibilityReason = (ticket = {}) => {
  if (String(ticket?.usage_status || "").toUpperCase() === "USED") {
    return "Already linked";
  }

  const normalizedDesignation = String(ticket?.designation || "")
    .trim()
    .replaceAll("_", " ")
    .toLowerCase();
  const normalizedTicketStatus = String(ticket?.weigh_bridge_ticket_status || "")
    .trim()
    .replaceAll("_", " ")
    .toLowerCase();

  if (normalizedDesignation && normalizedDesignation !== "not set") {
    return `Designation: ${getReadableGrnTicketText(ticket?.designation)}`;
  }

  if (normalizedTicketStatus && normalizedTicketStatus !== app_consts.OFFLOADING_GOODS) {
    return `Status: ${getReadableGrnTicketText(ticket?.weigh_bridge_ticket_status)}`;
  }

  return "";
};

const formatLastWeighbridgeSyncLabel = (syncMeta) => {
  if (!syncMeta?.syncedAt) {
    return "Last synced: Not yet";
  }

  const formattedTime = new Date(syncMeta.syncedAt).toLocaleString();
  const importedCountText =
    typeof syncMeta?.importedCount === "number"
      ? `, ${syncMeta.importedCount} ticket${syncMeta.importedCount === 1 ? "" : "s"} imported`
      : "";

  return `Last synced: ${formattedTime}${importedCountText}`;
};

const SHOULD_DEBUG_WEIGHBRIDGE = process.env.REACT_APP_DEBUG_WEIGHBRIDGE === "true";
const WEIGHBRIDGE_TICKET_CACHE_TTL_MS = 60 * 1000;

let goodsReceivedWeighbridgeTicketCache = {
  updatedAt: 0,
  tickets: [],
  context: null,
};

const isTicketCacheFresh = (updatedAt = 0) =>
  updatedAt > 0 && Date.now() - updatedAt < WEIGHBRIDGE_TICKET_CACHE_TTL_MS;

const CreateGoodsReceived = ({ page_type }) => {
  const dispatch = useDispatch();
  const { goods_received_ref_number } = useParams();
  const location = useLocation();
  const [page] = useState(page_type);
  const preselectedWeighbridgeTicket = location?.state?.selectedWeighbridgeTicket || null;
  const normalizedPreselectedWeighbridgeTicket = preselectedWeighbridgeTicket
    ? normalizeGrnSelectedTicket(preselectedWeighbridgeTicket)
    : null;
  const preloadedWeighbridgeContext = location?.state?.weighbridgeContext || null;

  const [showWeighbridgeModal, setShowWeighbridgeModal] = useState(false);
  const [selectedWeighbridgeTicket, setSelectedWeighbridgeTicket] = useState(normalizedPreselectedWeighbridgeTicket);
  const [weighbridgeTickets, setWeighbridgeTickets] = useState([]);
  const [weighbridgeContext, setWeighbridgeContext] = useState(preloadedWeighbridgeContext);
  const [loadingWeighbridgeTickets, setLoadingWeighbridgeTickets] = useState(false);
  const [weighbridgeError, setWeighbridgeError] = useState("");
  const [lastWeighbridgeSyncLabel, setLastWeighbridgeSyncLabel] = useState(
    formatLastWeighbridgeSyncLabel(getLastWeighbridgeSyncMeta()),
  );
  const [isWeighbridgeSelectionLocked, setIsWeighbridgeSelectionLocked] = useState(false);
  const inFlightWeighbridgeRequestRef = useRef(null);

  const isCreateMode = useMemo(() => !goods_received_ref_number, [goods_received_ref_number]);
  const displayWeighbridgeTickets = useMemo(() => weighbridgeTickets, [weighbridgeTickets]);

  useEffect(() => {
    if (!selectedWeighbridgeTicket || isWeighbridgeSelectionLocked) return;

    if (isSelectableGrnWeighbridgeTicket(selectedWeighbridgeTicket)) return;

    setSelectedWeighbridgeTicket(null);
    setWeighbridgeError(
      "The previously selected weighbridge ticket is no longer eligible for the GRN flow. Please choose another ticket.",
    );
  }, [isWeighbridgeSelectionLocked, selectedWeighbridgeTicket]);

  const loadWeighbridgeTickets = useCallback(async ({ silent = false, forceRefresh = false } = {}) => {
    const cachedTickets = goodsReceivedWeighbridgeTicketCache.tickets || [];
    const hasCachedTickets = cachedTickets.length > 0;
    const cacheIsFresh = hasCachedTickets && isTicketCacheFresh(goodsReceivedWeighbridgeTicketCache.updatedAt);

    if (hasCachedTickets) {
      setWeighbridgeTickets(cachedTickets);
      setWeighbridgeContext(goodsReceivedWeighbridgeTicketCache.context || null);
    }

    if (!forceRefresh && cacheIsFresh) {
      setWeighbridgeError("");
      setLoadingWeighbridgeTickets(false);
      return;
    }

    if (inFlightWeighbridgeRequestRef.current) {
      if (!silent) {
        setLoadingWeighbridgeTickets(true);
      }

      try {
        const cachedPayload = await inFlightWeighbridgeRequestRef.current;
        setWeighbridgeError("");
        setWeighbridgeTickets(cachedPayload.tickets);
        setWeighbridgeContext(cachedPayload.context);
      } catch (error) {
        if (!hasCachedTickets) {
          setWeighbridgeError(
            error?.response?.data?.message || "Failed to retrieve weighbridge tickets.",
          );
          setWeighbridgeTickets([]);
        }
      } finally {
        if (!silent || !hasCachedTickets) {
          setLoadingWeighbridgeTickets(false);
        }
      }
      return;
    }

    try {
      if (!silent || !hasCachedTickets) {
        setLoadingWeighbridgeTickets(true);
      }
      setWeighbridgeError("");

      inFlightWeighbridgeRequestRef.current = (async () => {
        const response = await makeEmployeeDashboardApiRequest(
          "get",
          RETRIEVE_ADD_GOODS_RECEIVED_FROM_NOT_SET_WEIGHBRIDGE_TICKET_DATA_API,
        );

        const responseData = response?.data?.data || response?.data || {};
        const nextWeighbridgeContext = extractNotSetWeighbridgeContext(responseData);
        const { rows, path: ticketSourcePath } = extractTicketList(nextWeighbridgeContext);

        const normalized = sortWeighbridgeTicketsDesc(normalizeWeighbridgeTickets(rows));
        const nextPayload = {
          tickets: normalized,
          context: nextWeighbridgeContext,
        };

        goodsReceivedWeighbridgeTicketCache = {
          updatedAt: Date.now(),
          tickets: normalized,
          context: nextWeighbridgeContext,
        };

        if (SHOULD_DEBUG_WEIGHBRIDGE) {
          const selectableTicketCount = normalized.filter((ticket) =>
            isSelectableGrnWeighbridgeTicket(ticket),
          ).length;
          const blockedTicketCount = normalized.length - selectableTicketCount;

          console.log("GRN WEIGHBRIDGE RAW RESPONSE =>", response?.data);
          console.log("GRN WEIGHBRIDGE CONTEXT =>", nextWeighbridgeContext);
          console.log("GRN WEIGHBRIDGE TICKET SOURCE PATH =>", ticketSourcePath || "not found");
          console.log("GRN WEIGHBRIDGE FIRST ROW =>", rows?.[0] || null);
          console.log("GRN WEIGHBRIDGE FIRST NORMALIZED ROW =>", normalized?.[0] || null);
          console.log("GRN WEIGHBRIDGE COUNTS =>", {
            totalLoaded: normalized.length,
            selectableTicketCount,
            blockedTicketCount,
            blockedExamples: normalized
              .filter((ticket) => !isSelectableGrnWeighbridgeTicket(ticket))
              .slice(0, 5)
              .map((ticket) => ({
                ticket_reference_no:
                  ticket?.weigh_bridge_ticket_reference_no || ticket?.weigh_bridge_ticket_number || "",
                reason: getGrnWeighbridgeIneligibilityReason(ticket),
              })),
          });
        }

        return nextPayload;
      })();

      const nextPayload = await inFlightWeighbridgeRequestRef.current;
      setWeighbridgeContext(nextPayload.context);
      setWeighbridgeTickets(nextPayload.tickets);
    } catch (error) {
      console.error("Retrieve weighbridge tickets error =>", error);
      if (error?.response?.status === 401) {
        dispatch(success_sign_out_modal());
      }
      if (!hasCachedTickets) {
        setWeighbridgeError(
          error?.response?.data?.message || "Failed to retrieve weighbridge tickets.",
        );
        setWeighbridgeTickets([]);
      } else {
        setWeighbridgeError("Showing cached tickets while the latest refresh failed.");
      }
    } finally {
      inFlightWeighbridgeRequestRef.current = null;
      if (!silent || !hasCachedTickets) {
        setLoadingWeighbridgeTickets(false);
      }
    }
  }, [dispatch]);

  useEffect(() => {
    if (!isCreateMode || isWeighbridgeSelectionLocked) {
      return;
    }

    void loadWeighbridgeTickets({ silent: true });
  }, [isCreateMode, isWeighbridgeSelectionLocked, loadWeighbridgeTickets]);

  const openWeighbridgeModal = useCallback(async () => {
    if (isWeighbridgeSelectionLocked) return;

    setLastWeighbridgeSyncLabel(formatLastWeighbridgeSyncLabel(getLastWeighbridgeSyncMeta()));
    setShowWeighbridgeModal(true);
    void loadWeighbridgeTickets();
  }, [isWeighbridgeSelectionLocked, loadWeighbridgeTickets]);

  const refreshWeighbridgeTickets = useCallback(() => {
    setLastWeighbridgeSyncLabel(formatLastWeighbridgeSyncLabel(getLastWeighbridgeSyncMeta()));
    void loadWeighbridgeTickets({ forceRefresh: true });
  }, [loadWeighbridgeTickets]);

  const closeWeighbridgeModal = useCallback(() => {
    setShowWeighbridgeModal(false);
  }, []);

  const handleSelectWeighbridgeTicket = useCallback((ticket) => {
    if (!isSelectableGrnWeighbridgeTicket(ticket) || isWeighbridgeSelectionLocked) {
      return;
    }

    setSelectedWeighbridgeTicket(normalizeGrnSelectedTicket(ticket));
    setWeighbridgeError("");
    setShowWeighbridgeModal(false);
  }, [isWeighbridgeSelectionLocked]);

  return (
    <>
      <Head
        title={`${goods_received_ref_number ? "Continue Processing" : "Create"} Physical Goods Received Ticket`}
      />

      <Content>
        <PageHeading
          title={
            `${goods_received_ref_number ? "Continue Processing" : "Create"} Physical Goods Received Ticket`
          }
          title_addition={goods_received_ref_number}
          description={
            "Process a Physical Goods Received Ticket from your Designated Warehouse."
          }
          backToText={`${
            goods_received_ref_number ? `${goods_received_ref_number} Ticket Details` : "Goods Received Tickets"
          }`}
          backToLink={`/dashboard/goods_received${goods_received_ref_number ? `/${goods_received_ref_number}` : ""}`}
        />

        {isCreateMode && (
          <div className="mb-3">
            <Button color="primary" onClick={openWeighbridgeModal} disabled={isWeighbridgeSelectionLocked}>
              {isWeighbridgeSelectionLocked
                ? "Weighbridge Ticket Locked"
                : selectedWeighbridgeTicket
                  ? "Change Weighbridge Ticket"
                  : "Select Weighbridge Ticket"}
            </Button>

            {isWeighbridgeSelectionLocked && (
              <Alert color="info" className="mt-3 mb-0">
                The linked weighbridge ticket is locked because this GRN is already in progress.
              </Alert>
            )}

            {selectedWeighbridgeTicket && (
              <SelectedWeighbridgeTicketCard
                ticket={selectedWeighbridgeTicket}
                flowLabel="goods received flow"
                weightDisplayMode="grn"
                onChangeTicket={isWeighbridgeSelectionLocked ? undefined : openWeighbridgeModal}
              />
            )}
          </div>
        )}

        <WizardComp
          type={page}
          selectedWeighbridgeTicket={selectedWeighbridgeTicket}
          weighbridgeContext={weighbridgeContext}
          onWeighbridgeSelectionLockChange={setIsWeighbridgeSelectionLocked}
        />
      </Content>

      <Modal isOpen={showWeighbridgeModal} toggle={closeWeighbridgeModal} size="xl" fullscreen>
        <ModalHeader toggle={closeWeighbridgeModal}>Select Weighbridge Ticket</ModalHeader>
        <ModalBody>
          <div className="text-soft small mb-3">{lastWeighbridgeSyncLabel}</div>

          {loadingWeighbridgeTickets && weighbridgeTickets.length === 0 ? (
            <AuthLoader loading_message="Loading Weighbridge Tickets..." />
          ) : weighbridgeError && weighbridgeTickets.length === 0 ? (
            <Alert color="danger" className="mb-0">
              {weighbridgeError}
            </Alert>
          ) : (
            <>
              {loadingWeighbridgeTickets && weighbridgeTickets.length > 0 && (
                <div className="text-soft small mb-3">Refreshing weighbridge tickets...</div>
              )}
              {weighbridgeError && weighbridgeTickets.length > 0 && (
                <Alert color="warning" className="mb-3">
                  {weighbridgeError}
                </Alert>
              )}
              <WeighbridgeTicketSelector
              tickets={displayWeighbridgeTickets}
              allTickets={weighbridgeTickets}
              onSelect={handleSelectWeighbridgeTicket}
              onClose={closeWeighbridgeModal}
              onRefresh={refreshWeighbridgeTickets}
              isRefreshing={loadingWeighbridgeTickets}
              isSelectableTicket={isSelectableGrnWeighbridgeTicket}
              getDisabledActionLabel={(ticket) => getGrnWeighbridgeIneligibilityReason(ticket) || "Not Eligible"}
                getSelectionStatusLabel={(ticket) =>
                  isSelectableGrnWeighbridgeTicket(ticket)
                    ? "Eligible"
                    : getGrnWeighbridgeIneligibilityReason(ticket) || "Not Eligible"
                }
                getSelectionStatusTone={(ticket) =>
                  isSelectableGrnWeighbridgeTicket(ticket) ? "success" : "warning"
                }
              />
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default CreateGoodsReceived;
